// パリィの音を「技の重さ」と「連続で弾いた段」で変える候補 (2026-10-13・開発版 ?parryPitch=p|q|r だけ・配布版は今の柳の型のまま)。
//   ユーザー原文 (10-13・検収パック 10-12e の自由記入)「パリィの音は軽いけど気持ちいい系。パリィの音は攻撃の重さとか
//   連撃のしのぎぐわいとかで音の音階？とかを変えてもっと気持ちよさを出してほしい」。
//   設計・手本・聞き比べ方 = docs/research/2026-10-13-parry-sound-pitch-by-weight-and-chain.md
//
//   p 音階で上がる = 連続で弾くたびに鳴りが五音音階を1つ上がる。重い技は低い所から＋二段目の「ゴン」。崩した瞬間は高い和音
//   q 和音が積み上がる = 続けて弾くたびに和音の音が1つずつ増える。重い技は和音ごと低く
//   r 控えめ = 今の柳の型を丸ごと半音ずつ高く。重い技は全体を低く
//   どれも「崩した瞬間」(弾いてボスの体幹が尽きた・parry_down) は決めの音。ボスの致命を弾いた時 (exec) は今のまま (押し返しの二段)。
//
//   何を鳴らすかの表 = src/parry_pitch_plan.ts (scripts/render_parry_pitch_sound.py が素材と一緒に書き出す)。
//   素材 = public/sfx/parry_pitch_{body,ring,heavy}.wav (開発版で選んだ時だけ読む)。読めていない時は今の音が鳴る。
//   どの案も効果音の名前は 'parry' のまま = プレイヤーの音の優先 (src/sound_priority.ts) はそのまま効く。
import { PARRY_PITCH_PLAN } from './parry_pitch_plan'
import { PARRY_SOUND_CANDIDATES, PARRY_SOUND_DEFAULT } from './parry_sound'
import type { ParryMaterial } from './parry_material'

export type ParryPitchVariant = 'off' | 'p' | 'q' | 'r'
export type ParryWeightTier = 'light' | 'mid' | 'heavy'

export const PARRY_PITCH_LABELS: Record<Exclude<ParryPitchVariant, 'off'>, string> = {
  p: '音階で上がる', q: '和音が積み上がる', r: '控えめ (半音ずつ)',
}

/** 開発版で読む素材 [キー, URL] (柳の型は本番の先読みが読む)。 */
export const PARRY_PITCH_FILES: readonly [string, string][] = ['body', 'ring', 'heavy']
  .map((k) => [`parry_pitch_${k}`, `/sfx/parry_pitch_${k}.wav?v=1`])

/** 技の重さ (hits.ts parryIntensityOf の 0.25〜1 = 体幹ダメージ基準・連撃なら +0.15) → 段。
 *  体幹 30 の技 (いちばん多い) = 軽・40 前後 = 中・50 以上 (連撃の 45 以上) = 重。 */
export function weightTier(weight: number): ParryWeightTier {
  return weight >= 0.75 ? 'heavy' : weight >= 0.45 ? 'mid' : 'light'
}

export function parseParryPitchVariant(raw: string | null | undefined): ParryPitchVariant {
  return raw === 'p' || raw === 'q' || raw === 'r' ? raw : 'off'
}
let variant: ParryPitchVariant = 'off'
export function setParryPitchVariant(raw: string | null | undefined): ParryPitchVariant {
  variant = parseParryPitchVariant(raw); return variant
}
export function parryPitchVariant(): ParryPitchVariant { return variant }

/** attackId = 弾いた技 (10-14 音の作り直し ?parrySfx= が材質と攻撃力を引く・src/parry_remake.ts・この候補は使わない)。 */
export type ParryPitchHit = { chain: number; weight: number; broke: boolean; attackId?: string; material?: ParryMaterial }
export type ParryPitchLayer = { sample: string; rate: number; gain: number }

/** 今の柳の型の大きさ (src/parry_sound.ts の gainDb)。表の倍率はこの上に重ねる。 */
const BASE_GAIN = 10 ** (PARRY_SOUND_CANDIDATES[PARRY_SOUND_DEFAULT].gainDb / 20)
export const PARRY_PITCH_REVERB = PARRY_SOUND_CANDIDATES[PARRY_SOUND_DEFAULT].reverbSend

/** 弾いた瞬間に鳴らす層。案を選んでいない時は null (= 今の音)。 */
export function planParryPitch(hit: ParryPitchHit, v: ParryPitchVariant = variant): ParryPitchLayer[] | null {
  if (v === 'off') return null
  const entry = PARRY_PITCH_PLAN.variants[v][planKey(hit)]
  return entry.layers.map((l) => ({ sample: l.sample, rate: l.rate, gain: l.gain * BASE_GAIN }))
}

export function planKey(hit: ParryPitchHit): keyof typeof PARRY_PITCH_PLAN.variants.p {
  const chain = Math.max(1, Math.min(5, Math.round(hit.chain) || 1))
  return `${weightTier(hit.weight)}:${hit.broke ? 'broke' : chain}` as keyof typeof PARRY_PITCH_PLAN.variants.p
}
